import { Upload, message } from "antd";
import type { RcFile, UploadFile, UploadProps } from "antd/es/upload/interface";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import VideoIcon from "../../../assets/icons/jouer.svg";
import LockIcon from "../../../assets/icons/lock-outlined.svg";
import PlusIcon from "../../../assets/icons/plus-black.svg";
import AddCircleGrey from "../../../assets/icons/add-circle-grey.svg";
import AudioThumbnail from "../../common/AudioThumbnail/AudioThumbnail";
import { fileService } from "../../../services/file.service";
import { compressImage, convertHeicFile, generateThumbnailURL, isImageAcceptedFormat } from "../../../utls/FunctionsUtil";
import "./ChatScriptFilesUpload.less";
import MailboxMediaModal from "../../MailboxMediaModal";
import RecordAudioModal from "../../RecordAudioModal/RecordAudioModal";
import SelectQuickFileAlbumModal from "../modals/SelectQuickFileAlbumModal/SelectQuickFileAlbumModal";
import SelectQuickFileMediaModal, { QuickFileMedia } from "../modals/SelectQuickFileMediaModal/SelectQuickFileMediaModal";

export type ChatScriptFilesUploadProps = {
  disabled?: boolean;
  maxCount?: number;
  defaultList?: any[];
  onFilesChange?: (fileList: UploadFile<[]>[]) => void;
  context?: "create" | "update";
  scriptId?: string;
  children?: React.ReactNode;
};

const ChatScriptFilesUpload: React.FC<ChatScriptFilesUploadProps> = ({
  disabled = false,
  maxCount,
  defaultList = [],
  onFilesChange,
  context = "create",
  scriptId,
  children,
}) => {
  const { t } = useTranslation();
  const [fileList, setFileList] = useState<UploadFile[]>(defaultList || []);

  useEffect(() => {
    setFileList(defaultList || []);
  }, [defaultList]);

  const [isMediaModal, setIsMediaModal] = useState(false);
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [showQuickAlbumsModal, setShowQuickAlbumsModal] = useState(false);
  const [selectedQuickAlbumId, setSelectedQuickAlbumId] = useState<string | null>(null);
  const [selectedAlbumType, setSelectedAlbumType] = useState<"media" | "audio">("media");
  const uploadRef = useRef<HTMLInputElement>(null);
  const previousFileListRef = useRef<UploadFile[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const params: any = new URLSearchParams(location.search);
  const paramsFiles: Array<any[]> = JSON.parse((params.get("selected") as any) || "[]");
  const [paramIds, setParamIds] = useState<any>([]);

  const notifyFileChange = useCallback(() => {
    if (onFilesChange && JSON.stringify(fileList) !== JSON.stringify(previousFileListRef.current)) {
      previousFileListRef.current = fileList;
      onFilesChange(fileList);
    }
  }, [fileList, onFilesChange]);

  useEffect(() => {
    notifyFileChange();
  }, [notifyFileChange]);

  useEffect(() => {
    if (paramsFiles?.length !== 0) {
      setParamIds(paramsFiles);
    }
    return () => {
      setParamIds([]);
    };
  }, [location.search]);

  useEffect(() => {
    if (paramIds !== null && paramIds !== undefined && paramIds?.length !== 0) {
      getAllSelectedFiles(paramIds);
    }
  }, [paramIds]);

  const getAllSelectedFiles = async (fileIds: Array<any>) => {
    const selectedFileIds = fileList.map((file) => file.uid);
    const filesToFetch = fileIds.filter((id) => !selectedFileIds.includes(id));
    await fileService.allSelectedFiles(filesToFetch).then(async (response: any) => {
      const filesToAdd: any = [];

      for (const item of response) {
        let fileType = "";
        let fileExt = "";
        let isVideo = false;
        let isAudio = false;

        if (item.url.includes(".mp4")) {
          fileType = "video/mp4";
          fileExt = "mp4";
          isVideo = true;
        } else if (item.url.includes(".mp3")) {
          fileType = "audio/mp3";
          fileExt = "mp3";
          isAudio = true;
        } else if (item.url.includes(".wav")) {
          fileType = "audio/wav";
          fileExt = "wav";
          isAudio = true;
        } else if (item.url.includes(".m4a")) {
          fileType = "audio/m4a";
          fileExt = "m4a";
          isAudio = true;
        } else if (item.url.includes(".ogg")) {
          fileType = "audio/ogg";
          fileExt = "ogg";
          isAudio = true;
        } else if (item.url.includes(".aac")) {
          fileType = "audio/aac";
          fileExt = "aac";
          isAudio = true;
        } else if (item.url.includes(".webm")) {
          fileType = "audio/webm";
          fileExt = "webm";
          isAudio = true;
        } else {
          fileType = "image/webp";
          fileExt = "webp";
        }

        const originFileObj2 = {
          isPrivate: true,
          status: "done",
          uid: item._id,
          name: item._id,
          percent: 0,
          thumbUrl: "",
          type: "quick-link",
        };
        const JsonString = JSON.stringify(originFileObj2);
        const blob = new Blob([JsonString], { type: fileType });
        const file = new File([blob], `newQuickFile.${fileExt}`, { type: fileType });

        let thumbUrl = "";
        if (isVideo) {
          thumbUrl = item?.thumbnailUrl || "";
        } else if (isAudio) {
          thumbUrl = "";
        } else {
          thumbUrl = item?.url || "";
        }

        filesToAdd.push({
          ...item,
          imgType: "quick-file",
          isPrivate: true,
          status: "done",
          uid: item._id,
          name: item._id,
          percent: 0,
          thumbUrl,
          type: fileType,
          originFileObj: file,
        });
      }

      setFileList((prevFiles: any) => {
        return [...prevFiles, ...filesToAdd];
      });
    });
  };

  const customItemRender = (originNode: React.ReactNode, file: any) => {
    const isVideo: boolean = file.type?.includes("video");
    const isAudio: boolean = file.type?.includes("audio");
    const lock: boolean = !!file.isPrivate;

    return (
      <div className="clickable-image-wrapper" onClick={() => onRemove(file)}>
        {isAudio ? <AudioThumbnail /> : originNode}
        {lock && (
          <img
            src={LockIcon}
            width={isAudio ? 20 : 30}
            style={{
              position: "relative",
              top: isAudio ? -150 : -90,
              right: isAudio ? -70 : -35,
              color: "white",
            }}
            alt="lock icon"
          />
        )}
        {isVideo && (
          <img
            src={VideoIcon}
            style={!lock ? { top: -147, right: -73 } : { top: -160, right: -45 }}
            alt="video icon"
            className="chat-script-video-icon"
          />
        )}
        <div className="remove-overlay">
          <span className="remove-icon">×</span>
        </div>
      </div>
    );
  };

  const beforeUpload: UploadProps["beforeUpload"] = async (file: RcFile): Promise<File | boolean | string> => {
    try {
      if (isImageAcceptedFormat(file.type)) {
        const compressedImage = await compressImage(file);
        if (compressedImage !== null) handleConvertedFile(file.uid, compressedImage, true);
        return false;
      } else if (file.type === "image/heic" || file.type === "") {
        handleConvertedFile(file.uid, file, false);
        const convertedFile = await convertHeicFile(file);
        const compressedImage = await compressImage(convertedFile);
        if (compressedImage !== null) handleConvertedFile(file.uid, compressedImage, true);
        return false;
      } else if (file.type.includes("video/")) {
        handleConvertedFile(file.uid, file, true);
        return false;
      } else if (file.type.includes("audio/")) {
        handleConvertedFile(file.uid, file, true);
        return false;
      } else {
        message.error(t("unsupported-file-format-error-message"));
        return Upload.LIST_IGNORE;
      }
    } catch (error) {
      console.error(error);
      removeUnsupportedFiles(file.uid);
      message.error(t("unsupported-file-format-error-message"));
      return Upload.LIST_IGNORE;
    }
  };

  const handleConvertedFile = async (uid: string, file: File, converted: boolean) => {
    if (!converted) {
      const fileObj: UploadFile = { uid, name: file.name, status: "uploading", percent: 50 };
      setFileList((prevList) => [fileObj, ...prevList]);
    } else {
      const fileObj: UploadFile = { uid, name: file.name, status: "done", type: file.type, size: file.size, originFileObj: file as RcFile };
      (fileObj as any).isPrivate = true;
      if (file.type.includes("video")) {
        const result = await generateThumbnailURL(file as RcFile);
        fileObj.url = result;
      } else if (file.type.includes("audio")) {
        fileObj.url = "";
      }
      setFileList((prevList) => [fileObj, ...prevList.filter((f) => f.uid !== fileObj.uid)]);
    }
  };

  const isImageUrl = (file: UploadFile): boolean => {
    if (file.type?.includes("audio/")) {
      return false;
    }
    return true;
  };

  const removeUnsupportedFiles = (uid: string) => {
    setFileList((prevList) => [...prevList.filter((file) => file.uid !== uid)]);
  };
  const onRemove = async (file: UploadFile<any>) => {
    setFileList((prev: any) => prev.filter((f: any) => f.uid !== file.uid));
  };

  const handleDeviceClick = () => {
    setIsMediaModal(false);
    setTimeout(() => uploadRef.current?.click(), 100);
  };

  const handleOpenAudioRecorder = () => {
    setIsMediaModal(false);
    setTimeout(() => {
      setShowAudioModal(true);
    }, 100);
  };

  const handleAudioReady = async (audioBlob: Blob, audioTitle: string) => {
    const audioFile = new File([audioBlob], `${audioTitle || "audio_recording"}.mp3`, { type: "audio/mpeg" });

    const uid = `upload_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;

    handleFileUpload(uid, audioFile as RcFile);

    setShowAudioModal(false);
  };

  const handleQuickFilesClick = () => {
    setIsMediaModal(false);
    setShowQuickAlbumsModal(true);
  };

  const handleQuickFilesConfirm = (selectedFiles: QuickFileMedia[]) => {
    const filesToAdd: any[] = [];
    selectedFiles.forEach((item: QuickFileMedia) => {
      let fileType = "";
      let fileExt = "";
      if (item.isVideo) {
        fileType = "video/mp4";
        fileExt = "mp4";
      } else if (item.isAudio) {
        if (item.url?.includes(".wav")) {
          fileType = "audio/wav";
          fileExt = "wav";
        } else if (item.url?.includes(".m4a")) {
          fileType = "audio/m4a";
          fileExt = "m4a";
        } else if (item.url?.includes(".ogg")) {
          fileType = "audio/ogg";
          fileExt = "ogg";
        } else {
          fileType = "audio/mp3";
          fileExt = "mp3";
        }
      } else {
        fileType = "image/webp";
        fileExt = "webp";
      }

      const originFileObj = {
        isPrivate: true,
        status: "done",
        uid: item.id,
        name: item.id,
        percent: 0,
        thumbUrl: "",
        type: "quick-link",
      };
      const JsonString = JSON.stringify(originFileObj);
      const blob = new Blob([JsonString], { type: fileType });
      const file = new File([blob], `newQuickFile.${fileExt}`, { type: fileType });

      filesToAdd.push({
        uid: item.id,
        name: item.id,
        imgType: "quick-file",
        isPrivate: true,
        status: "done",
        percent: 0,
        thumbUrl: item.thumbnail || item.url,
        type: fileType,
        url: item.url,
        originFileObj: file,
      });
    });

    setFileList((prevFiles: any) => {
      const existingIds = new Set(prevFiles.map((f: any) => f.uid));
      const newFiles = filesToAdd.filter((f: any) => !existingIds.has(f.uid));
      return [...prevFiles, ...newFiles];
    });

    setSelectedQuickAlbumId(null);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      const uid = `upload_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      handleFileUpload(uid, file as RcFile);
    });
    e.target.value = "";
  };

  const handleFileUpload = async (uid: string, file: RcFile) => {
    try {
      if (isImageAcceptedFormat(file.type)) {
        const compressedImage = await compressImage(file);
        if (compressedImage !== null) handleConvertedFile(uid, compressedImage, true);
      } else if (file.type === "image/heic" || file.type === "") {
        handleConvertedFile(uid, file, false);
        const convertedFile = await convertHeicFile(file);
        const compressedImage = await compressImage(convertedFile);
        if (compressedImage !== null) handleConvertedFile(uid, compressedImage, true);
      } else if (file.type.includes("video/")) {
        handleConvertedFile(uid, file, true);
      } else if (file.type.includes("audio/")) {
        handleConvertedFile(uid, file, true);
      } else {
        message.error(t("unsupported-file-format-error-message"));
      }
    } catch (error) {
      console.error(error);
      removeUnsupportedFiles(uid);
      message.error(t("unsupported-file-format-error-message"));
    }
  };

  return (
    <div className={`chat-script-upload ${fileList.length === 0 ? "empty" : "with-files"}`}>
      {fileList.length === 0 ? (
        <div className="chat-script-upload__empty" onClick={() => setIsMediaModal(true)}>
          {(!maxCount || fileList.length < (maxCount || 0)) && <img src={AddCircleGrey} width={150} height={150} alt="add" />}
        </div>
      ) : (
        <div className="chat-script-upload__files">
          <div className="chat-script-upload__add-button" onClick={() => setIsMediaModal(true)}>
            {maxCount ? (
              fileList.length < maxCount && (
                <div className={`add-circle ${disabled ? "disabled" : ""}`}>
                  <img src={PlusIcon} width={30} alt="plus" />
                </div>
              )
            ) : (
              <div className={`add-circle ${disabled ? "disabled" : ""}`}>
                <img src={PlusIcon} width={30} alt="plus" />
              </div>
            )}
          </div>

          <div className="chat-script-upload__file-list">
            <Upload
              accept="image/*,video/*,audio/*,.mp3,.wav,.m4a,.aac,.ogg,.webm"
              disabled={disabled}
              action=""
              itemRender={(originNode: any, file: any) => customItemRender(originNode, file)}
              defaultFileList={defaultList}
              fileList={fileList}
              listType="picture-card"
              maxCount={maxCount}
              onRemove={onRemove}
              beforeUpload={beforeUpload}
              isImageUrl={isImageUrl}
              showUploadList={{ showRemoveIcon: true, showPreviewIcon: false }}
              multiple
            >
              <div></div>
            </Upload>
          </div>

          {children}
        </div>
      )}

      <input
        type="file"
        ref={uploadRef}
        style={{ display: "none" }}
        multiple
        accept="image/*,video/*,audio/*,.mp3,.wav,.m4a,.aac,.ogg,.webm"
        onChange={handleFileInputChange}
      />

      {isMediaModal && (
        <MailboxMediaModal
          isOpen={isMediaModal}
          onClose={() => setIsMediaModal(false)}
          onMyAlbums={handleQuickFilesClick}
          onMyDevice={handleDeviceClick}
          // onRecordAudio={handleOpenAudioRecorder}
        />
      )}

      {showQuickAlbumsModal && (
        <SelectQuickFileAlbumModal
          onClose={() => setShowQuickAlbumsModal(false)}
          onAlbumSelect={(albumId, type) => {
            setSelectedQuickAlbumId(albumId);
            setSelectedAlbumType(type);
            setShowQuickAlbumsModal(false);
          }}
        />
      )}

      {selectedQuickAlbumId && (
        <SelectQuickFileMediaModal
          albumId={selectedQuickAlbumId}
          type={selectedAlbumType}
          onClose={() => {
            setSelectedQuickAlbumId(null);
            setSelectedAlbumType("media");
          }}
          onConfirm={handleQuickFilesConfirm}
          onBack={() => {
            setSelectedQuickAlbumId(null);
            setSelectedAlbumType("media");
            setShowQuickAlbumsModal(true);
          }}
        />
      )}

      <RecordAudioModal isOpen={showAudioModal} onClose={() => setShowAudioModal(false)} onAudioReady={handleAudioReady} isFromConv={false} />
    </div>
  );
};

export default ChatScriptFilesUpload;
